import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';
import useIsMobile from '../hooks/useIsMobile';
import PageMeta from '../components/PageMeta';
import { takeFlash } from '../utils/flash';

const CoachLogin = () => {
  const navigate = useNavigate();
  const { loginCoach } = useAuth();
  const isMobile = useIsMobile();
  const styles = isMobile ? mobileStyles(baseStyles) : baseStyles;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // X-17: a message left by the page that signed us out (account deleted).
  // Read after mount so the prerendered HTML stays the same.
  const [notice, setNotice] = useState('');
  useEffect(() => { const m = takeFlash(); if (m) setNotice(m); }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.coachLogin({ email, password });
      const { token, coach } = response.data;
      loginCoach(token, coach);
      navigate('/coach/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <PageMeta
        title="Coach Sign In | HoopHero"
        description="Sign in to your HoopHero coach account to manage your roster, schedule, and player payments."
        path="/coach/login"
      />
      <div style={styles.box}>
        <img src="/logo-wide.png" alt="HoopHero" style={styles.logoImg} />
        <h1 style={{ margin: 0, fontWeight: 400, ...styles.subtitle }}>Coach / Admin Sign In</h1>

        <form onSubmit={handleLogin} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="coach-login-email">Email Address</label>
            <input
              id="coach-login-email"
              autoComplete="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="coach@email.com"
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="coach-login-password">Password</label>
            <input
              id="coach-login-password"
              autoComplete="current-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="••••••••"
              required
            />
          </div>
          {notice && <div role="status" style={{ background: '#E4F5ED', color: '#14855A', padding: '10px 12px', borderRadius: '7px', fontSize: '13px', marginBottom: '12px' }}>{notice}</div>}
          {error && <div style={styles.error}>{error}</div>}
          <button type="submit" style={styles.btn} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={styles.footer}>
          <button style={styles.link} onClick={() => navigate('/coach/forgot-password')}>
            Forgot password?
          </button>
          <span style={styles.footerSep}>·</span>
          <button style={styles.link} onClick={() => navigate('/')}>
            ← Back to Portal Select
          </button>
        </div>
      </div>
    </div>
  );
};

const baseStyles = {
  container: {
    minHeight: '100vh',
    background: '#F6F7FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  box: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
  },
  logo: {
    fontFamily: 'Georgia, serif',
    fontSize: '32px',
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: '6px',
    color: '#0D1117',
  },
  logoImg: {
    display: 'block',
    width: '300px',
    maxWidth: '100%',
    height: 'auto',
    margin: '0 auto 10px',
  },
  accent: { color: '#C2460B' },
  subtitle: {
    textAlign: 'center',
    fontSize: '14px',
    color: '#5A6070',
    marginBottom: '28px',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '5px' },
  label: { fontSize: '12px', fontWeight: '600', color: '#5A6070' },
  input: {
    padding: '10px 12px',
    border: '1px solid rgba(0,0,0,0.13)',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  },
  error: {
    background: '#FDEAEA',
    color: '#C93535',
    padding: '10px 12px',
    borderRadius: '8px',
    fontSize: '13px',
  },
  btn: {
    background: '#C2460B',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  footer: { textAlign: 'center', marginTop: '20px' },
  footerSep: { color: '#C2C7D0', margin: '0 8px', fontSize: '13px' },
  link: {
    background: 'none',
    border: 'none',
    color: '#1A5FD4',
    cursor: 'pointer',
    fontSize: '13px',
  },
};

const mobileStyles = (s) => ({
  ...s,
  box: { ...s.box, padding: '28px 22px' },
  logo: { ...s.logo, fontSize: '26px' },
  subtitle: { ...s.subtitle, fontSize: '13px', marginBottom: '22px' },
});

export default CoachLogin;